import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { homePathForRole, isApprover, useAuth } from './AuthContext'
import { Loading } from '../components/ui'
import type { AppRole } from '../types/db'

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, mustChangePassword, signedOutByUser } = useAuth()
  const location = useLocation()

  if (status === 'loading') return <Loading label="Checking your session" />
  if (status === 'signedOut') {
    // Remember the page only for an interrupted session. After a tap on Sign
    // out there is nothing to resume, and whoever signs in next gets their own
    // home rather than the previous user's screen.
    const from = signedOutByUser ? undefined : location.pathname + location.search
    return <Navigate to="/login" replace state={from ? { from } : undefined} />
  }
  if (mustChangePassword && location.pathname !== '/set-password') {
    return <Navigate to="/set-password" replace />
  }
  return <>{children}</>
}

/**
 * Role guard. The database enforces the real rules through RLS, so a coach who
 * types an admin URL sees an empty screen either way. This just keeps the app
 * honest and sends people somewhere useful instead of somewhere blank.
 */
export function RequireRole({
  roles,
  children,
}: {
  roles: AppRole[]
  children: ReactNode
}) {
  const { role, status } = useAuth()
  if (status === 'loading') return <Loading />
  if (!role || !roles.includes(role)) {
    return <Navigate to={homePathForRole(role)} replace />
  }
  return <>{children}</>
}

/**
 * Approver guard: Michel, or a coach he has delegated approval to. Mirrors
 * is_approver() in SQL, which is what the qualifications RLS checks. Gina and
 * Pascal are core team but not approvers, so they are sent to their own home.
 */
export function RequireApprover({ children }: { children: ReactNode }) {
  const { profile, role, status } = useAuth()
  if (status === 'loading') return <Loading />
  if (!isApprover(profile)) {
    return <Navigate to={homePathForRole(role)} replace />
  }
  return <>{children}</>
}
